import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Upload, File, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface FileUploadSectionProps {
  currentFileUrl?: string | null;
  onFileUpload: (file: File) => Promise<void>;
  disabled?: boolean;
}

export function FileUploadSection({ 
  currentFileUrl, 
  onFileUpload, 
  disabled 
}: FileUploadSectionProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Accepted file types for sequences
  const acceptedTypes = [
    '.fseq',    // xLights
    '.lms',     // Light-O-Rama
    '.seq',     // Light-O-Rama
    '.xml',     // Vixen
    '.zip',     // Zip archives
    '.rar',     // RAR archives
    '.7z'       // 7-Zip archives
  ];

  const maxFileSize = 50 * 1024 * 1024; // 50MB (UI limit)

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize) {
      return `File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds the 50MB limit`;
    }

    // Check file extension
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedTypes.includes(fileExtension)) {
      return `File type "${fileExtension}" is not supported. Accepted types: ${acceptedTypes.join(', ')}`;
    }

    return null;
  };

  const handleFileSelect = async (file: File) => {
    if (uploading) {
      toast.error('An upload is already in progress. Please wait until it finishes.');
      return;
    }
    const validationError = validateFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      let progressInterval: ReturnType<typeof setInterval> | null = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            if (progressInterval) clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      await onFileUpload(file);
      
      if (progressInterval) clearInterval(progressInterval);
      setUploadProgress(100);
      
      setTimeout(() => {
        setUploadProgress(0);
        setUploading(false);
      }, 1000);
      
    } catch (error) {
      // Ensure the progress interval is cleared on error
      setUploadProgress(0);
      setUploading(false);
      setUploading(false);
      setUploadProgress(0);
      // Error handling is done in the parent component
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled || uploading) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 1) {
      toast.error('Please upload one file at a time');
      return;
    }

    if (files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (uploading) return;
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
    // Reset the input
    e.target.value = '';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileNameFromUrl = (url: string) => {
    // If we store a storage_path (e.g., userId/filename.ext), fall back to last segment
    return (url || '').split('/').pop() || 'Unknown file';
  };

  const getStoragePathFromUrl = (url: string) => {
    try {
      if (url.startsWith('http')) {
        const u = new URL(url);
        let p = u.pathname;
        const prefixes = [
          '/storage/v1/object/public/sequences/',
          '/storage/v1/object/sign/sequences/',
          '/storage/v1/object/authenticated/sequences/',
          '/storage/v1/object/sequences/'
        ];
        for (const pref of prefixes) {
          if (p.startsWith(pref)) {
            p = p.slice(pref.length);
            break;
          }
        }
        p = p.replace(/^\/*/, '');
        if (p.startsWith('public/')) p = p.slice('public/'.length);
        return p;
      }
      // Already a storage path
      return url.replace(/^\/*/, '');
    } catch {
      return url;
    }
  };

  const handleViewCurrentFile = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!currentFileUrl) return;
    try {
      const storagePath = getStoragePathFromUrl(currentFileUrl);
      // Use createSignedUrls to avoid failure for private bucket without broad select
      const { data, error } = await supabase.storage
        .from('sequences')
        .createSignedUrls([storagePath], 60 * 60);
      const signed = data?.[0]?.signedUrl;
      if (!error && signed) {
        window.open(signed, '_blank', 'noopener');
        return;
      }
      window.open(currentFileUrl, '_blank', 'noopener');
    } catch {
      window.open(currentFileUrl, '_blank', 'noopener');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Sequence File Upload
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current File Display */}
        {currentFileUrl && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-green-800">Current File:</p>
                  <p className="text-sm text-green-700 truncate">
                    {getFileNameFromUrl(currentFileUrl)}
                  </p>
                  <a
                    href="#"
                    onClick={handleViewCurrentFile}
                    className="text-sm text-green-600 hover:underline"
                  >
                    View File
                  </a>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </div>
        )}

        {/* Drag & Drop Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging 
              ? 'border-primary bg-primary/5' 
              : disabled 
                ? 'border-gray-200 bg-gray-50' 
                : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
        >
          <div className="space-y-4">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
              disabled ? 'bg-gray-100' : 'bg-gray-100'
            }`}>
              <Upload className={`h-8 w-8 ${disabled ? 'text-gray-400' : 'text-gray-600'}`} />
            </div>
            
            <div>
              <h3 className={`text-lg font-medium ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>
                {disabled ? 'Select a sequence first' : 'Upload sequence file'}
              </h3>
              <p className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
                Drag & drop your file here, or click to browse
              </p>
            </div>

            <Button
              variant="outline"
              disabled={disabled || uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <File className="h-4 w-4 mr-2" />
              Choose File
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept={acceptedTypes.join(',')}
              onChange={handleFileInputChange}
              className="hidden"
              disabled={disabled}
            />
          </div>
        </div>

        {/* File Requirements */}
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-medium">File Requirements:</p>
              <ul className="text-sm space-y-1">
                <li>• Maximum file size: 50MB</li>
                <li>• Supported formats: {acceptedTypes.join(', ')}</li>
                <li>• Files will be auto-renamed to avoid conflicts</li>
                <li>• Zip/RAR archives are recommended for complete shows</li>
              </ul>
            </div>
          </AlertDescription>
        </Alert>

        {/* Supported Formats */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Supported Formats:</p>
          <div className="flex flex-wrap gap-2">
            {acceptedTypes.map((type) => (
              <Badge key={type} variant="outline" className="text-xs">
                {type}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}